import { useMemo, useState } from 'react';
import { AlertTriangle, Check, Copy, Download, Smartphone } from 'lucide-react';
import { chargeUtileEpc, virementDeFacture, VirementInvalide } from '../../lib/payments/epc';
import { qrEnSvg } from '../../lib/payments/qr';
import { euros } from '../../lib/orders/devis';

/*
  Le QR de paiement, et ce qui l'entoure.

  ## Ce qu'un QR peut, et ce qu'il ne peut pas

  Le code pré-remplit un virement dans l'application bancaire : bénéficiaire,
  IBAN, montant, communication. Rien n'est débité en le scannant — le parent
  valide ensuite avec ses propres moyens.

  **Sur le téléphone qui affiche l'écran, il ne sert à rien** : on ne scanne
  pas son propre écran. Et il n'existe aujourd'hui **aucun lien universel** qui
  ouvrirait l'application bancaire belge avec un virement pré-rempli — ni
  schéma normalisé, ni lien profond commun aux banques. Le seul mécanisme de ce
  genre en Belgique est Payconiq, qui suppose un contrat commerçant.

  Ce qui fait réellement gagner du temps sur un téléphone, c'est donc autre
  chose : **recopier chaque champ en un geste**. Trois boutons de copie valent
  mieux qu'un lien qui n'ouvrirait rien.
*/

export interface QrDePaiementProps {
  communication: string;
  remainingCents: number;
  /** Pour nommer le fichier enregistré. */
  invoiceNumber?: string | null;
}

const IBAN_LISIBLE = 'BE64 3631 1005 7452';

export function QrDePaiement({ communication, remainingCents, invoiceNumber }: QrDePaiementProps) {
  const [copie, setCopie] = useState<string | null>(null);

  const { svg, erreur } = useMemo(() => {
    try {
      const charge = chargeUtileEpc(virementDeFacture(communication, remainingCents));
      return { svg: qrEnSvg(charge, { taillePx: 260 }), erreur: null as string | null };
    } catch (e) {
      /*
        On n'affiche PAS de QR approximatif. Un code que la banque refuse
        laisse le parent devant un échec qu'il ne peut pas comprendre ; les
        coordonnées écrites en toutes lettres, elles, marchent toujours.
      */
      return {
        svg: null,
        erreur: e instanceof VirementInvalide ? e.message : 'Le code n\'a pas pu être produit.',
      };
    }
  }, [communication, remainingCents]);

  const copier = async (valeur: string, quoi: string) => {
    try {
      await navigator.clipboard.writeText(valeur);
      setCopie(quoi);
      setTimeout(() => setCopie((c) => (c === quoi ? null : c)), 2000);
    } catch {
      /* Un navigateur qui refuse le presse-papiers : le texte reste lisible. */
      setCopie(null);
    }
  };

  const enregistrer = () => {
    if (!svg) return;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paiement-${invoiceNumber ?? 'eclosion'}.svg`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const Champ = ({ libelle, valeur, aCopier, mono }: {
    libelle: string; valeur: string; aCopier: string; mono?: boolean;
  }) => (
    <div className="flex items-center justify-between gap-3 border-b border-gray-100 py-2 last:border-b-0">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-gray-500">{libelle}</p>
        <p className={`truncate text-sm text-gray-900 ${mono ? 'font-mono font-semibold' : ''}`}>
          {valeur}
        </p>
      </div>
      <button
        onClick={() => void copier(aCopier, libelle)}
        className="flex flex-shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        aria-label={`Copier ${libelle}`}
      >
        {copie === libelle
          ? <><Check className="h-3.5 w-3.5 text-green-600" /> Copié</>
          : <><Copy className="h-3.5 w-3.5" /> Copier</>}
      </button>
    </div>
  );

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <h3 className="mb-3 font-semibold text-gray-900">Payer par virement</h3>

      <div className="flex flex-col gap-5 sm:flex-row">
        {/* --- Le code --- */}
        <div className="flex flex-shrink-0 flex-col items-center">
          {svg ? (
            <>
              <div
                className="rounded-lg border border-gray-200 bg-white p-2"
                dangerouslySetInnerHTML={{ __html: svg }}
              />
              <button
                onClick={enregistrer}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                <Download className="h-3.5 w-3.5" /> Enregistrer le code
              </button>
            </>
          ) : (
            <div className="flex h-[260px] w-[260px] items-center justify-center rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-center text-sm text-amber-800">
                <AlertTriangle className="mx-auto mb-2 h-5 w-5" />
                {erreur} Les coordonnées ci-contre restent valables.
              </p>
            </div>
          )}
        </div>

        {/* --- Les coordonnées, copiables --- */}
        <div className="min-w-0 flex-grow">
          <Champ libelle="Bénéficiaire" valeur="Éclosion ASBL" aCopier="Eclosion ASBL" />
          <Champ libelle="IBAN" valeur={IBAN_LISIBLE} aCopier={IBAN_LISIBLE} mono />
          <Champ libelle="Montant" valeur={euros(remainingCents)}
                 aCopier={(remainingCents / 100).toFixed(2)} />
          <Champ libelle="Communication" valeur={communication} aCopier={communication} mono />

          {/*
            Ce que le QR vaut vraiment, dit sans exagérer. Un parent qui essaie
            de scanner l'écran qu'il tient en main et n'y arrive pas croira que
            quelque chose est cassé.
          */}
          <div className="mt-3 flex items-start gap-2 rounded-md bg-gray-50 p-2.5 text-xs text-gray-600">
            <Smartphone className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <p>
              <span className="font-medium text-gray-700">Depuis un ordinateur :</span> ouvrez
              l'application de votre banque et scannez le code — le virement se remplit tout seul.
              {' '}
              <span className="font-medium text-gray-700">Depuis ce téléphone :</span> utilisez
              plutôt les boutons « Copier ». On ne peut pas scanner l'écran qu'on tient en main.
            </p>
          </div>

          <p className="mt-2 text-xs text-gray-500">
            La communication est <strong>structurée</strong> : votre banque la vérifie à la saisie
            et refusera une faute de frappe.
          </p>
        </div>
      </div>
    </div>
  );
}

export default QrDePaiement;
